<script>
import imageService from "@/services/image";

export default {
  name: "ImagesSelect",
  props: {
    value: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
  },

  data() {
    return {
      categories: [],
      images: [],
      filteredImages: [],
      categoryImages: [],
      isLoading: false,
      catalog: [],
      filterValue: "",
      selectedCategory: null,
    };
  },

  computed: {
    selectedInternal() {
      return Array.isArray(this.value) ? this.value : [];
    },

    selectedDescriptions() {
      if (!this.selectedInternal.length) return ["Sin datos"];

      return this.selectedInternal.map((id) => {
        const selectedImage =
          this.catalog.find((img) => this.getImageId(img) === id) ||
          this.images.find((img) => this.getImageId(img) === id) ||
          this.filteredImages.find((img) => this.getImageId(img) === id) ||
          this.$store.state.cacheImages?.[id];

        return selectedImage
          ? `${selectedImage.category} - ${selectedImage.description}`
          : "Sin datos";
      });
    },
  },

  methods: {
    getImageId(image) {
      return image?._id || image?.id || "";
    },

    normalizeText(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    },

    getImageUniqueKey(image) {
      const category = this.normalizeText(image?.category);
      const description = this.normalizeText(image?.description);

      if (category || description) {
        return `${category}|${description}`;
      }

      return this.getImageId(image);
    },

    getUniqueImages(images = []) {
      const byDisplayKey = new Map();

      images.forEach((image) => {
        const uniqueKey = this.getImageUniqueKey(image);
        if (uniqueKey && !byDisplayKey.has(uniqueKey)) {
          byDisplayKey.set(uniqueKey, image);
        }
      });

      return Array.from(byDisplayKey.values());
    },

    async loadCatalog() {
      this.isLoading = true;
      try {
        const categories = await imageService.getCategories();

        this.categories = [
          ...new Set(categories.map((c) => String(c).trim())),
        ].sort((a, b) => {
          const nameA = a.toLowerCase();
          const nameB = b.toLowerCase();

          if (nameA.includes("otros") && !nameB.includes("otros")) return 1;
          if (!nameA.includes("otros") && nameB.includes("otros")) return -1;

          return nameA.localeCompare(nameB);
        });

        this.catalog = this.getUniqueImages(await imageService.getImages());
        this.images = [...this.catalog];
        this.filteredImages = [...this.images];
      } catch (error) {
        console.error("Error loading images:", error);
        this.categories = [];
        this.catalog = [];
        this.images = [];
        this.filteredImages = [];
      } finally {
        this.isLoading = false;
      }
    },

    async loadImagesByCategory(category) {
      this.isLoading = true;
      try {
        if (category && category !== "") {
          this.categoryImages = this.getUniqueImages(
            await imageService.getImages("", category)
          );
        } else {
          this.categoryImages = [...this.images];
        }
        this.filteredImages = [...this.categoryImages];
      } catch (error) {
        console.error("Error loading images by category:", error);
        this.categoryImages = [];
        this.filteredImages = [];
      } finally {
        this.isLoading = false;
      }
    },

    filterImages(filterValue) {
      this.filterValue = filterValue;
      // Base estable: el set completo de la categoría (o el catálogo si no hay
      // categoría). Nunca filtrar sobre filteredImages, que ya viene reducida.
      const baseImages = this.selectedCategory
        ? this.categoryImages
        : this.images;
      if (!filterValue) {
        this.filteredImages = [...baseImages];
      } else {
        this.filteredImages = baseImages.filter((img) =>
          img.description.toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    },

    async filterImagesByCategory() {
      await this.loadImagesByCategory(this.selectedCategory);
    },

    // La categoría es solo un filtro: la orden guarda únicamente las imágenes.
    // Al montar con imágenes ya elegidas (p. ej. al recuperar un borrador), si
    // todas son de la misma categoría se vuelve a mostrar esa.
    async inferCategoryFromSelection() {
      if (this.selectedCategory || !this.selectedInternal.length) return;
      const categories = new Set(
        this.selectedInternal.map((id) => {
          const image =
            this.catalog.find((img) => this.getImageId(img) === id) ||
            this.$store.state.cacheImages?.[id];
          return image?.category ? String(image.category).trim() : null;
        })
      );
      if (categories.size !== 1) return;
      const [category] = categories;
      if (!category || !this.categories.includes(category)) return;
      this.selectedCategory = category;
      await this.filterImagesByCategory();
    },

    onInput(values) {
      const selectedValues = [
        ...new Set(
          (Array.isArray(values) ? values : [values])
            .map((value) =>
              typeof value === "object" ? this.getImageId(value) : value
            )
            .filter(Boolean)
        ),
      ];

      selectedValues.forEach((id) => {
        const selected =
          this.filteredImages.find((img) => this.getImageId(img) === id) ||
          this.catalog.find((img) => this.getImageId(img) === id);

        if (selected && !this.$store.state.cacheImages?.[id]) {
          this.$store.dispatch("addImages", selected);
        }
      });

      this.$emit("input", selectedValues);

      // Keep the dropdown open to continue selecting multiple images.
      this.$nextTick(() => {
        if (this.$refs.imagesSelect?.visible === false) {
          this.$refs.imagesSelect.visible = true;
        }
      });
    },

    removeSelection(id) {
      const newSelection = this.selectedInternal.filter(
        (selectedId) => selectedId !== id
      );
      this.$emit("input", newSelection);
    },
  },

  async created() {
    await this.loadCatalog();
    await this.inferCategoryFromSelection();
  },
};
</script>

<template lang="pug">
div
  label.label Categoría
  el-select(
    v-model="selectedCategory"
    filterable
    placeholder="Seleccionar categoría"
    :disabled="isDisabled"
    @change="filterImagesByCategory"
    :loading="isLoading"
    clearable
  )
    el-option(v-for="category in categories" :key="category" :label="category" :value="category")

  template(v-if="!readonly")
    label.label Imagen
    el-select(
      ref="imagesSelect"
      :value="selectedInternal"
      @input="onInput"
      multiple
      filterable
      collapse-tags
      :filter-method="filterImages"
      :loading="isLoading"
      :disabled="isDisabled"
      placeholder="Seleccionar imágen"
    )
      el-option(
        v-for="item in filteredImages"
        :key="getImageId(item)"
        :label="item.description"
        :value="getImageId(item)"
      )
        span(style="float: left") {{ item.category + " - " + item.description }}

  div(v-if="selectedInternal.length > 0" class="selected-container")
    div(v-for="id in selectedInternal" :key="id" class="selection-item")
      span {{ selectedDescriptions[selectedInternal.indexOf(id)] }}
      button.button-invisible-new(@click="removeSelection(id)" :disabled="isDisabled")
        micon(:name="'clear'")

  div(v-else-if="readonly")
    p(v-for="(description, index) in selectedDescriptions" :key="index") {{ description }}
</template>

<style scoped>
.label {
  font-weight: 600;
  margin-right: 8px;
}
</style>
